import React from 'react';
import { type FileRejection } from 'react-dropzone';
import { DropzoneWrapper } from '@/components/elements/DropzoneWrapper';
import {
	CTAScreen,
	DropImage,
	ImageTextSection,
	Reviews,
} from '@/components/landing/reusableSections';
import { Button } from '@/components/UI';
import {
	useLandingShownEvent,
	useSendToAmplitudeScrolledPercent,
	useTranslationWithLocale,
	useUploadImage,
} from '@/hooks';
import {
	AmplitudeService,
	CookiesStorageService,
	GenerateLandingLinkService,
	GoogleService,
} from '@/services';
import {
	benefitsFilterGhibli,
	funnelReviewsFilterGhibli,
	howWorkFilterGhibli,
	reviewsLanding,
	whyLiftFilterGhibli,
} from '@/data';
import {
	AMPLITUDE_EVENTS,
	GOOGLE_EVENTS,
	LANDING_AMPLITUDE_EVENTS,
	LANDING_CONSTANTS,
	LANDING_COOKIE_STORAGE,
	LOCAL_STORAGE_KEYS,
} from '@/constants';
import benefit from '@/public/images/ai-filters/ghibli/benefit.webp';
import funnelImg1 from '@/public/images/ai-filters/ghibli/funnelImg1.webp';
import funnelImg2 from '@/public/images/ai-filters/ghibli/funnelImg2.webp';
import howItWork from '@/public/images/ai-filters/ghibli/howWork.webp';
import whyLift from '@/public/images/ai-filters/ghibli/whyLift.webp';
import ctaScreen from '@/public/images/landing/ctaScreen.webp';
import styles from './GhibliLanding.module.scss';

const GhibliLanding = () => {
	// Translations
	const { t } = useTranslationWithLocale('ai-filters-ghibli');
	const { t: tReviews } = useTranslationWithLocale('landing-reviews');

	// States
	const [loading, setLoading] = React.useState(false);

	// Logic
	const uploadImage = useUploadImage();

	// Helpers
	const isLoading = loading || uploadImage.isLoading;

	// Actions
	const handleDrop = (
		acceptedFiles: File[],
		fileRejections: FileRejection[],
	) => {
		if (fileRejections.length > 0) {
			console.warn('Rejected files:', fileRejections);
			return;
		}

		setLoading(true);
		uploadImage.mutate(
			{ file: acceptedFiles[0] },
			{
				onSuccess: async ({ viewUrl }) => {
					CookiesStorageService.set(
						LANDING_COOKIE_STORAGE.IMAGE_FROM_LANDING,
						viewUrl,
					);
					GoogleService.logEvent(GOOGLE_EVENTS.LANDING_PASSED);
					await AmplitudeService.logEventAsync(AMPLITUDE_EVENTS.MEDIA_ADDED, {
						context: LANDING_AMPLITUDE_EVENTS.AI_FILTERS_GHIBLI_LANDING,
					});
					setLoading(false);

					window.location.href =
						GenerateLandingLinkService.funnelImageAiFilterGhibli();
				},
				onError: () => {
					setLoading(false);
				},
			},
		);
	};
	const openGalleryEvent = () => {
		AmplitudeService.logEvent(AMPLITUDE_EVENTS.MEDIA_GALLERY_SHOWN, {
			context: LANDING_AMPLITUDE_EVENTS.AI_FILTERS_GHIBLI_LANDING,
		});
	};

	// Analytics
	useSendToAmplitudeScrolledPercent(
		[50, 75, 100],
		LOCAL_STORAGE_KEYS.AI_FILTERS_GHIBLI_SCROLLED_POINTS,
		AMPLITUDE_EVENTS.WEBAPP_LANDING_SCROLLED,
		{ type: LANDING_AMPLITUDE_EVENTS.AI_FILTERS_GHIBLI },
	);
	useLandingShownEvent({ type: LANDING_AMPLITUDE_EVENTS.AI_FILTERS_GHIBLI });
	return (
		<>
			<DropImage
				imageLeft={{
					src: funnelImg1,
					alt: 'filter preview',
				}}
				imageRight={{
					src: funnelImg2,
					alt: 'filter preview',
				}}
				t={t}
				isLoading={isLoading}
				dropzoneOptions={{
					disabled: isLoading,
					multiple: false,
					accept: LANDING_CONSTANTS.ACCEPTED_IMAGE_TYPES_DROPZONE,
					maxFiles: LANDING_CONSTANTS.MAX_FILES,
					maxSize: LANDING_CONSTANTS.MAX_FILE_SIZE,
					onDrop: handleDrop,
					onFileDialogOpen: openGalleryEvent,
				}}
				title={t('heroData.title')}
				subtitle={t('heroData.subtitle')}
				reviewsData={funnelReviewsFilterGhibli}
				classes={{
					dropdownClass: styles['ghibliLanding__drop-image-container'],
				}}
			/>

			{/* HowItWork */}
			<ImageTextSection
				title={t('howItWorkData.mainTitle')}
				image={howItWork}
				data={howWorkFilterGhibli}
				t={t}
				buttonNode={
					<DropzoneWrapper
						options={{
							multiple: false,
							accept: LANDING_CONSTANTS.ACCEPTED_IMAGE_TYPES_DROPZONE,
							maxFiles: LANDING_CONSTANTS.MAX_FILES,
							maxSize: LANDING_CONSTANTS.MAX_FILE_SIZE,
							onDrop: handleDrop,
							onFileDialogOpen: openGalleryEvent,
						}}
					>
						<Button
							as='button'
							preset='landing'
							text={t('getStarted')}
							size='large'
							classes={{
								button: styles['ghibliLanding__drop-wrapper-button'],
							}}
						/>
					</DropzoneWrapper>
				}
			/>

			<Reviews t={tReviews} data={reviewsLanding} />

			{/* Benefit */}
			<ImageTextSection
				title={t('benefitData.mainTitle')}
				image={benefit}
				specialListItem
				data={benefitsFilterGhibli}
				t={t}
				buttonNode={
					<DropzoneWrapper
						options={{
							multiple: false,
							accept: LANDING_CONSTANTS.ACCEPTED_IMAGE_TYPES_DROPZONE,
							maxFiles: LANDING_CONSTANTS.MAX_FILES,
							maxSize: LANDING_CONSTANTS.MAX_FILE_SIZE,
							onDrop: handleDrop,
							onFileDialogOpen: openGalleryEvent,
						}}
					>
						<Button
							as='button'
							preset='landing'
							text={t('getStarted')}
							size='large'
							classes={{
								button: styles['ghibliLanding__drop-wrapper-button'],
							}}
						/>
					</DropzoneWrapper>
				}
			/>

			{/* WhyLift */}
			<ImageTextSection
				title={t('whyLift.mainTitle')}
				image={whyLift}
				data={whyLiftFilterGhibli}
				t={t}
				classes={{
					imageContainer: styles['ghibliLanding__whyLift-image-container'],
					list: styles['ghibliLanding__whyLift-list'],
				}}
				buttonNode={
					<DropzoneWrapper
						options={{
							multiple: false,
							accept: LANDING_CONSTANTS.ACCEPTED_IMAGE_TYPES_DROPZONE,
							maxFiles: LANDING_CONSTANTS.MAX_FILES,
							maxSize: LANDING_CONSTANTS.MAX_FILE_SIZE,
							onDrop: handleDrop,
							onFileDialogOpen: openGalleryEvent,
						}}
					>
						<Button
							as='button'
							preset='landing'
							text={t('getStarted')}
							size='large'
							classes={{
								button: styles['ghibliLanding__drop-wrapper-button'],
							}}
						/>
					</DropzoneWrapper>
				}
			/>

			{/* TryLift */}
			<CTAScreen
				title1={t('tryLiftData.title1')}
				title_green={t('tryLiftData.greenText')}
				title2={t('tryLiftData.title2')}
				subtitle={t('tryLiftData.subtitle')}
				image={ctaScreen}
				altText={t('tryLiftData.altText')}
				buttonText={t('getStarted')}
				buttonNode={
					<DropzoneWrapper
						options={{
							multiple: false,
							accept: LANDING_CONSTANTS.ACCEPTED_IMAGE_TYPES_DROPZONE,
							maxFiles: LANDING_CONSTANTS.MAX_FILES,
							maxSize: LANDING_CONSTANTS.MAX_FILE_SIZE,
							onDrop: handleDrop,
							onFileDialogOpen: openGalleryEvent,
						}}
					>
						<Button
							as='button'
							preset='landing_outline'
							text={t('getStarted')}
							size='large'
							classes={{
								button: styles['ghibliLanding__cta-drop-wrapper-button'],
							}}
						/>
					</DropzoneWrapper>
				}
			/>
		</>
	);
};

export default GhibliLanding;
